import React, { useState, useEffect } from 'react';
import { Modal, Banner, FormField } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { AuthUser } from '@/types/auth';

interface UserEditDialogProps {
  open: boolean;
  user: AuthUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  first_name: string;
  last_name: string;
  role: string;
  account_scope: string;
  user_type: string;
  is_active: boolean;
  source_user_id: string;
}

const ROLE_HINTS: Record<string, string> = {
  admin: 'Full access to all operations',
  super: 'Can view data within the selected account scope',
  normal: 'Standard user access',
};

const UserEditDialog: React.FC<UserEditDialogProps> = ({ open, user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    first_name: '', last_name: '', role: 'normal', account_scope: 'all', user_type: 'RM', is_active: true, source_user_id: '',
  });
  const [errors, setErrors] = useState<{ first_name?: string; last_name?: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role,
        account_scope: user.account_scope,
        user_type: user.user_type,
        is_active: user.is_active,
        source_user_id: user.source_user_id || '',
      });
      setErrors({});
      setApiError(null);
    }
  }, [user]);

  const validate = () => {
    const e: { first_name?: string; last_name?: string } = {};
    if (!formData.first_name.trim()) e.first_name = 'First name is required';
    if (!formData.last_name.trim()) e.last_name = 'Last name is required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!user || !validate()) return;
    setLoading(true); setApiError(null);
    try {
      const payload: any = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        account_scope: formData.role === 'super' ? formData.account_scope : 'all',
        user_type: formData.user_type,
        is_active: formData.is_active,
      };
      if (formData.source_user_id.trim()) payload.source_user_id = formData.source_user_id.trim();
      await apiService.updateUser(user.id, payload);
      onSuccess(); onClose();
    } catch (err: any) {
      setApiError(err.message || 'Failed to update user');
    } finally { setLoading(false); }
  };

  const handleClose = () => { if (!loading) { setErrors({}); setApiError(null); onClose(); } };

  const setField = (field: 'first_name' | 'last_name' | 'role' | 'account_scope' | 'user_type' | 'source_user_id') =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field as 'first_name' | 'last_name']) {
        setErrors(prev => { const n = { ...prev }; delete n[field as 'first_name' | 'last_name']; return n; });
      }
    };

  if (!user) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Edit User"
      size="md"
      footer={
        <>
          <LCBtn variant="outline" size="sm" onClick={handleClose} disabled={loading}>Cancel</LCBtn>
          <LCBtn variant="primary" size="sm" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </LCBtn>
        </>
      }
    >
      {apiError && <Banner variant="error" onClose={() => setApiError(null)} className="mb-4">{apiError}</Banner>}

      <div className="flex flex-col gap-3.5">
        <FormField label="Email" hint="Email cannot be changed after creation">
          <input className={`${CLS.input} opacity-60`} value={user.email} disabled readOnly />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="First Name" required error={errors.first_name}>
            <input className={CLS.input} value={formData.first_name} onChange={setField('first_name')} disabled={loading} />
          </FormField>
          <FormField label="Last Name" required error={errors.last_name}>
            <input className={CLS.input} value={formData.last_name} onChange={setField('last_name')} disabled={loading} />
          </FormField>
        </div>

        <FormField label="Role" required hint={ROLE_HINTS[formData.role]}>
          <select className={CLS.input} value={formData.role} onChange={setField('role')} disabled={loading}>
            <option value="normal">Normal</option>
            <option value="super">Super (Read-only)</option>
            <option value="admin">Admin (Full Access)</option>
          </select>
        </FormField>

        {formData.role === 'super' && (
          <FormField label="Account scope" required hint="Applies to all account views, totals and alert emails.">
            <select aria-label="Account scope" className={CLS.input} value={formData.account_scope} onChange={setField('account_scope')} disabled={loading}>
              <option value="all">All accounts</option>
              <option value="avpu">AVPU only</option>
              <option value="lcp">LCP only</option>
            </select>
          </FormField>
        )}

        <FormField label="User Type" required hint="Determines portfolio access permissions">
          <select className={CLS.input} value={formData.user_type} onChange={setField('user_type')} disabled={loading}>
            <option value="RM">RM (Relationship Manager)</option>
            <option value="IC">IC (Investment Counselor)</option>
            <option value="ARM">ARM (Assistant RM)</option>
            <option value="ADMIN">ADMIN</option>
            <option value="SUPER">SUPER</option>
            <option value="COMPLIANCE">COMPLIANCE</option>
            <option value="OPS">OPS</option>
          </select>
        </FormField>

        <FormField label="Source User ID" hint="Links to upstream system user_access_info.user_id">
          <input className={CLS.input} value={formData.source_user_id} onChange={setField('source_user_id')} disabled={loading} />
        </FormField>

        <div className="flex items-center justify-between py-2.5 px-3.5 border border-black/[0.12] bg-[#FAF9F8]">
          <div>
            <div className="text-[12.5px] font-semibold text-[#333]">Account Status</div>
            <div className="text-[11px] text-[#7A7A76] mt-0.5">
              {formData.is_active ? 'User can log in' : 'User cannot log in'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
            disabled={loading}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              formData.is_active ? 'bg-[#1f8a4a]' : 'bg-[#CCC6BE]'
            }`}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
              formData.is_active ? 'translate-x-4' : 'translate-x-0.5'
            }`} />
          </button>
        </div>

        <Banner variant="info" className="">
          <strong>Note:</strong> To change the password, use the "Reset Password" action in the user list.
        </Banner>
      </div>
    </Modal>
  );
};

export default UserEditDialog;
